/**
 * The one way the web app talks to the API. Every path starts with the API prefix exactly once. A 401 means the session
 * has ended: the app is told, and shows sign-in. Errors carry the server's code, its parameters and the i18n key.
 *
 * PROVENANCE: C:\Olivia\web\src\lib\api.ts @ f92475e, copied 2026-10-12 (the SIGNED_OUT_EVENT pattern of
 * C:\Gap-Assessor\src\lib\api.ts @ 2023d32). CHANGES: Pascal's event name; no upload or stream helpers yet.
 */

export const SIGNED_OUT_EVENT = 'pascal:signed-out'

/**
 * A refusal or failure from the server: its status, its code with the parameters the screen puts in the reader's
 * language (lib/errors.ts), and the i18n key. The message is the server's English sentence, for the console only: no
 * screen prints it.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly key: string,
    message: string,
    readonly params: Readonly<Record<string, string | number>> = {},
  ) {
    super(message)
  }
}

export function apiPath(path: string): string {
  const clean = path.replace(/^\/+/, '').replace(/^api\//, '')
  return `/api/${clean}`
}

async function send<T>(method: string, path: string, body: BodyInit | undefined, headers: Record<string, string>): Promise<T> {
  const res = await fetch(apiPath(path), { method, credentials: 'same-origin', headers, body })
  const text = await res.text()
  let data: unknown = null
  try {
    data = text ? JSON.parse(text) : null
  } catch {
    data = null
  }
  if (!res.ok) {
    const d = (data ?? {}) as { code?: string; key?: string; error?: string; params?: Record<string, string | number> }
    if (res.status === 401 && !path.startsWith('auth/') && !path.startsWith('/auth/')) window.dispatchEvent(new Event(SIGNED_OUT_EVENT))
    throw new ApiError(res.status, d.code ?? 'INTERNAL', d.key ?? 'errors.INTERNAL', d.error ?? res.statusText, d.params ?? {})
  }
  return data as T
}

function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  return send<T>(method, path, body === undefined ? undefined : JSON.stringify(body), body === undefined ? {} : { 'content-type': 'application/json' })
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
}
